import { MouseEvent, useState } from "react";
import { Link, generatePath } from "react-router-dom";
import { Avatar } from "src/components/atoms/Avatar/Avatar";
import { Text } from "src/components/atoms/Text/Text";
import * as Team from "src/state/modules/team";
import { ROUTES } from "src/utils/constants/routes";
import { teamAvatarUrl } from "src/utils/team-avatar";
import {
  RecentTeam,
  clearRecentlyViewed,
  getRecentlyViewed,
  removeRecentlyViewed,
} from "src/utils/recently-viewed";
import Styles from "./RecentlyViewed.module.scss";

const capitalize = (value?: string): string => (value ? value.charAt(0).toUpperCase() + value.slice(1) : "");

const formatMeta = (team: RecentTeam): string => {
  const staff = Array.isArray(team.staff) ? team.staff.join(", ") : team.staff;
  return [
    `${capitalize(team.seasonName)} ${team.seasonYear ?? ""}`.trim(),
    team.city && team.state ? `${team.city}, ${team.state}` : team.state,
    staff ? `Staff: ${staff}` : undefined,
  ]
    .filter(Boolean)
    .join(" • ");
};

/** GameChanger-style "Recently Viewed" team list, shown on the search page
 *  until the user starts typing. Backed by localStorage (see recently-viewed.ts). */
export const RecentlyViewed = () => {
  const [teams, setTeams] = useState<RecentTeam[]>(getRecentlyViewed);

  if (teams.length === 0) return null;

  const handleRemove = (event: MouseEvent, id: string) => {
    event.preventDefault();
    event.stopPropagation();
    setTeams(removeRecentlyViewed(id));
  };

  return (
    <section className={Styles.section} aria-label="Recently viewed teams">
      <div className={Styles.header}>
        <Text size="medium" weight="bold" color="cool-grey-dark">
          Recently Viewed
        </Text>
        <button type="button" className={Styles.clearAll} onClick={() => setTeams(clearRecentlyViewed())}>
          Clear All
        </button>
      </div>
      <div className={Styles.list}>
        {teams.map((team) => (
          <Link
            key={team.id}
            to={generatePath(ROUTES.teams.home, { id: team.id })}
            className={Styles.row}
            data-testid="recently-viewed-row"
          >
            <Avatar.Team
              className={Styles.avatar}
              sportIcon={(team.sport as Team.Sport) ?? Team.Sport.BASEBALL}
              source={teamAvatarUrl(team.id)}
              size="medium"
            />
            <div className={Styles.info}>
              <Text weight="bold" color="off-black" block className={Styles.teamName}>
                {team.name}
              </Text>
              <Text size="xsmall" color="cool-grey-dark" block className={Styles.meta}>
                {formatMeta(team)}
              </Text>
            </div>
            <button
              type="button"
              className={Styles.remove}
              onClick={(event) => handleRemove(event, team.id)}
              aria-label={`Remove ${team.name} from recently viewed`}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </Link>
        ))}
      </div>
    </section>
  );
};
